import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, MapPin, Star, Phone, Clock, Search, GitCompareArrows, X, Check } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import { getMe } from '../services/api';
import api from '../services/api';

export default function AllFacilitiesPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [compareList, setCompareList] = useState([]);

  useEffect(() => {
    const checkAuth = async () => {
      try { const r = await getMe(); setUser(r.data); } catch { setUser(null); }
    };
    checkAuth();
  }, []);

  useEffect(() => { loadFacilities(); }, [filterType]);

  const loadFacilities = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('facility_type', filterType);
      if (search) params.append('search', search);
      const response = await api.get(`/facilities?${params.toString()}`);
      setFacilities(response.data || []);
    } catch (error) {
      console.error('Error loading facilities:', error);
    } finally { setLoading(false); }
  };

  const handleSearch = (e) => { e.preventDefault(); loadFacilities(); };

  const toggleCompare = (fac, e) => {
    e.stopPropagation();
    setCompareList(prev => {
      const exists = prev.find(item => item.facility_id === fac.facility_id);
      if (exists) return prev.filter(item => item.facility_id !== fac.facility_id);
      if (prev.length >= 3) return prev;
      return [...prev, fac];
    });
  };

  const isSelected = (id) => compareList.some(c => c.facility_id === id);

  const TYPES = ["Clinic", "Dental Clinic", "Optical Clinic", "Hospital", "Multi-specialty Hospital"];

  const getTypeColor = (type) => {
    const colors = {
      'Clinic': 'bg-blue-100 text-blue-700', 'Dental Clinic': 'bg-purple-100 text-purple-700',
      'Optical Clinic': 'bg-teal-100 text-teal-700', 'Hospital': 'bg-emerald-100 text-emerald-700',
      'Multi-specialty Hospital': 'bg-amber-100 text-amber-700'
    };
    return colors[type] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Navbar user={user} onLogout={() => setUser(null)} />
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-manrope font-bold text-4xl text-[#0F2A44] mb-2" data-testid="facilities-page-title">Healthcare Facilities</h1>
          <p className="font-public text-[#4B5563]">Browse {facilities.length} registered healthcare facilities across Kenya</p>
        </div>

        {/* Search & Filters */}
        <div className="bg-white rounded-xl border border-slate-100 p-4 mb-8 flex flex-col md:flex-row gap-4" data-testid="facility-filters">
          <form onSubmit={handleSearch} className="flex-1 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or location..." className="pl-10 h-11" data-testid="facility-search-input" />
            </div>
            <button type="submit" className="bg-[#0F2A44] text-white px-4 rounded-lg hover:bg-[#0F2A44]/90 transition-colors" data-testid="facility-search-btn">
              <Search className="h-4 w-4" />
            </button>
          </form>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="h-11 px-3 rounded-lg border border-slate-200 text-sm min-w-[160px]" data-testid="facility-type-filter">
            <option value="">All Types</option>
            {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {/* Compare hint */}
        {facilities.length > 1 && compareList.length === 0 && (
          <div className="mb-4 px-4 py-2 bg-[#0F2A44]/5 rounded-lg text-sm text-[#4B5563] flex items-center gap-2" data-testid="compare-hint">
            <GitCompareArrows className="h-4 w-4 text-[#2DD4BF]" />
            Select up to 3 facilities to compare side-by-side
          </div>
        )}

        {loading && (
          <div className="flex justify-center items-center min-h-[40vh]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F2A44]"></div>
          </div>
        )}

        {!loading && facilities.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-100 p-12 text-center">
            <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="font-public text-[#4B5563] mb-2">No facilities found.</p>
            <p className="text-sm text-slate-400">Try adjusting your search or filters.</p>
          </div>
        )}

        {!loading && facilities.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {facilities.map((facility) => {
              const selected = isSelected(facility.facility_id);
              return (
                <div
                  key={facility.facility_id}
                  className={`bg-white rounded-xl border-2 overflow-hidden cursor-pointer transition-all ${selected ? 'border-[#2DD4BF] shadow-lg shadow-[#2DD4BF]/10' : 'border-slate-100 hover:border-slate-200'}`}
                  data-testid={`facility-card-${facility.facility_id}`}
                >
                  {/* Compare Checkbox */}
                  <div className="relative">
                    <button
                      onClick={(e) => toggleCompare(facility, e)}
                      className={`absolute top-3 right-3 z-10 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                        selected ? 'bg-[#2DD4BF] text-white' : 'bg-white/80 backdrop-blur text-slate-500 hover:bg-white hover:text-[#2DD4BF]'
                      } ${!selected && compareList.length >= 3 ? 'opacity-40 cursor-not-allowed' : ''}`}
                      data-testid={`compare-checkbox-${facility.facility_id}`}
                      disabled={!selected && compareList.length >= 3}
                    >
                      {selected ? <Check className="h-4 w-4" /> : <GitCompareArrows className="h-4 w-4" />}
                    </button>

                    {facility.facility_images?.length > 0 ? (
                      <img src={facility.facility_images[0]} alt={facility.facility_name} className="w-full h-40 object-cover" onClick={() => navigate(`/facility/${facility.facility_id}`)} />
                    ) : (
                      <div className="w-full h-40 bg-gradient-to-br from-[#0F2A44] to-[#1a3d5c] flex items-center justify-center" onClick={() => navigate(`/facility/${facility.facility_id}`)}>
                        <Building2 className="h-12 w-12 text-white/30" />
                      </div>
                    )}
                  </div>

                  <div className="p-5" onClick={() => navigate(`/facility/${facility.facility_id}`)}>
                    <span className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full mb-3 ${getTypeColor(facility.facility_type)}`}>{facility.facility_type}</span>
                    {facility.hospital_level && <span className="inline-block ml-2 px-2.5 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-600">{facility.hospital_level.split(' - ')[0]}</span>}

                    <h3 className="font-manrope font-bold text-lg text-[#0F2A44] mb-2">{facility.facility_name}</h3>

                    <div className="flex items-center gap-2 text-sm text-[#4B5563] mb-2">
                      <MapPin className="h-4 w-4 flex-shrink-0" />
                      <span className="font-public truncate">{facility.location}, {facility.region}</span>
                    </div>

                    <div className="flex items-center gap-2 text-sm text-[#4B5563] mb-2">
                      <Clock className="h-4 w-4 flex-shrink-0" />
                      <span className="font-public">{facility.is_24_7 ? '24/7' : facility.opening_hours?.weekdays || 'Contact for hours'}</span>
                    </div>

                    {(facility.average_rating > 0 || facility.total_reviews > 0) && (
                      <div className="flex items-center gap-1 text-sm mb-2">
                        <Star className="h-4 w-4 fill-[#2DD4BF] text-[#2DD4BF]" />
                        <span className="font-space font-medium text-[#262626]">{(facility.average_rating || 0).toFixed(1)}</span>
                        <span className="text-xs text-[#4B5563]">({facility.total_reviews || 0})</span>
                      </div>
                    )}

                    {facility.signature_services?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-slate-100">
                        {facility.signature_services.map((svc, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-[#2DD4BF]/10 text-[#0F2A44] text-xs rounded-full font-medium">{svc}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Floating Compare Bar */}
      {compareList.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-[#0F2A44] text-white py-4 px-6 shadow-2xl z-40" data-testid="compare-bar">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
              <GitCompareArrows className="h-5 w-5 text-[#2DD4BF]" />
              <span className="font-manrope font-semibold">{compareList.length} selected</span>
              <div className="flex items-center gap-2">
                {compareList.map(f => (
                  <div key={f.facility_id} className="flex items-center gap-1 bg-white/10 rounded-full px-3 py-1">
                    <span className="text-sm truncate max-w-[120px]">{f.facility_name}</span>
                    <button onClick={(e) => toggleCompare(f, e)} className="hover:text-red-400 transition-colors">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setCompareList([])} className="text-sm text-white/60 hover:text-white transition-colors" data-testid="compare-clear">Clear all</button>
              <Button
                onClick={() => {
                  const ids = compareList.map(f => f.facility_id).join(',');
                  navigate(`/facilities/compare?ids=${ids}`);
                }}
                disabled={compareList.length < 2}
                data-testid="compare-btn"
                className="bg-[#2DD4BF] hover:bg-[#2DD4BF]/90 text-[#0F2A44] font-semibold rounded-full px-6 disabled:opacity-50"
              >
                Compare ({compareList.length})
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
